import { createRouter, createWebHistory } from 'vue-router'
import { authService } from '@/services/auth'
import { useAppStore } from '@/stores/useAppStore'
import { isDevModeEnabled } from '@/utils/devMode'
import { createScopedLogger } from '@/utils/logger'
import {
	detectBitrix24Mode,
	ensureAuthentication,
	ensureBitrixEntryRoute,
	ensureRoleAccess,
	redirectAuthenticatedGuest,
} from './guards'
import { commonRoutes } from './routes/common.routes'
import { authRoutes } from './routes/auth.routes'
import { teacherRoutes } from './routes/teacher.routes'
import { adminRoutes } from './routes/admin.routes'
import { studentRoutes } from './routes/student.routes'

const routes = [
	...commonRoutes.slice(0, 1),
	...authRoutes,
	...studentRoutes,
	...teacherRoutes,
	...adminRoutes,
	...commonRoutes.slice(1),
]

const router = createRouter({
	history: createWebHistory(),
	routes,

	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) return savedPosition

		if (to.hash) {
			if (to.name === 'StudentDashboard') {
				return { top: 0 }
			}

			return {
				el: to.hash,
				behavior: 'smooth',
			}
		}

		return { top: 0 }
	},
})

const DEFAULT_TITLE =	'МеталлТрейд Академия — корпоративная платформа обучения'
const CHUNK_RELOAD_GUARD_KEY = 'app:chunk-reload-triggered'
const routerLogger = createScopedLogger('Router')
const routerGuardLogger = createScopedLogger('Router Guard')

const scrollPageToTop = () => {
	if (typeof window !== 'undefined') {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
	}
	if (typeof document === 'undefined') return
	document.documentElement.scrollTop = 0
	if (document.body) {
		document.body.scrollTop = 0
	}
}

router.afterEach((to) => {
	scrollPageToTop()

	if (typeof window !== 'undefined') {
		try {
			window.sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY)
		} catch (error) {
			routerLogger.warn('Failed to clear chunk reload guard:', error)
		}
	}

	if (typeof document === 'undefined') return

	const rawTitle = to?.meta?.title
	const pageTitle = typeof rawTitle === 'function' ? rawTitle(to) : rawTitle
	document.title = typeof pageTitle === 'string' && pageTitle.trim() ? pageTitle.trim() : DEFAULT_TITLE
})

router.beforeEach(async (to, from, next) => {
	const appStore = useAppStore()
	const devModeActive = isDevModeEnabled()
	const isBitrix24Mode = detectBitrix24Mode()

	routerGuardLogger.log('Navigation:', {
		from: from.path,
		to: to.path,
		requiresAuth: to.meta.requiresAuth,
		isAuthenticated: authService.isAuthenticated(),
		timestamp: new Date().toISOString(),
	})

	if (!ensureBitrixEntryRoute({ to, next, isBitrix24Mode })) return

	if (
		!(await ensureAuthentication({
			to,
			next,
			authService,
			appStore,
			devModeActive,
			isBitrix24Mode,
			logger: routerGuardLogger,
		}))
	) {
		return
	}

	if (
		!(await ensureRoleAccess({
			to,
			next,
			appStore,
			devModeActive,
			isBitrix24Mode,
			logger: routerGuardLogger,
		}))
	) {
		return
	}

	if (!redirectAuthenticatedGuest({ to, next, authService, appStore })) return

	next()
})

router.onError((error) => {
	const message = String(error?.message || '')
	const isChunkLoadError =
		/ChunkLoadError/i.test(message) ||
		/Loading chunk [\d]+ failed/i.test(message) ||
		/Failed to fetch dynamically imported module/i.test(message) ||
		/Importing a module script failed/i.test(message)

	if (!isChunkLoadError || typeof window === 'undefined') return

	try {
		const alreadyRetried = window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1'
		if (alreadyRetried) {
			console.error('[Router] Chunk loading failed after retry. Manual refresh is required.', error)
			return
		}

		window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1')

		const url = new URL(window.location.href)
		url.searchParams.set('_v', String(Date.now()))
		window.location.replace(url.toString())
	} catch (guardError) {
		console.error('[Router] Failed to recover from chunk load error:', guardError)
		window.location.reload()
	}
})

export default router